import { deleteCookie, getCookie, setCookie } from "@/services/cookies";
import { IndexedDb, IndexedDbKey } from "@/services/indexedDb";
import type { UserToken } from "@/services/user/userTypes";

// __Host- prefix enforces Secure + path=/ + no Domain — only valid over HTTPS.
// On HTTP (local dev) we fall back to a plain name.
const CookieName = import.meta.env.PROD ? "__Host-token" : "colectio_token";
const ExpirationDays = 30;

/**
 * Handles token storing and retrieval in/from browser cookie.
 *
 * Cookie storage is chosen over IndexedDB for forward-compatibility with Nuxt SSR:
 * the server can read the cookie on initial request via `useCookie()`, eliminating
 * the need for a client-side async token lookup on every page load.
 */
export const TokenService = {
  get: (): UserToken | null => {
    return getCookie(CookieName);
  },

  store: (token: UserToken): void => {
    const expiration = new Date();
    expiration.setDate(expiration.getDate() + ExpirationDays);
    setCookie(CookieName, token, { expiration: expiration.toUTCString(), path: "/", secure: import.meta.env.PROD });
  },

  delete: (): void => {
    deleteCookie(CookieName);
  },
};

/**
 * One-time migration: moves token from IndexedDB (old storage) to cookie (new storage).
 * Called once on app startup. Safe to call repeatedly — no-ops if nothing to migrate.
 * Can be removed once all users have been migrated (after a few releases).
 */
export async function migrateTokenFromIndexedDb(): Promise<void> {
  const indexedDbToken = await IndexedDb.get(IndexedDbKey.UserToken);
  if (!indexedDbToken) {
    return;
  }

  if (!TokenService.get()) {
    TokenService.store(indexedDbToken as UserToken);
  }

  await IndexedDb.delete(IndexedDbKey.UserToken);
}
